// course.reducer.ts
import { Action, createReducer, on } from '@ngrx/store';
import {
  CurrentCourseHeirarchyActions,
  updateCurrentCourseHeirarchyStructure,
  MoveCourseItemActions,
  clearCurrentCourse,
  CreateCourseItemActions,
  ApplyTemplateToCourseAction,
  UpdateCourseSettingsActions,
  CurriculumSeedProductActions,
  GenerateCourseAiHierarchyActions,
  GenerateLessonAiContentActions,
} from './course.actions';
import {
  CourseDetailsModelBase,
  CourseHeirarchyModel,
  CourseHierarchyItemTypeEnum,
  StudentItem,
} from 'src/app/components/user-dashboard/product/models/course';
import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { SessionActions } from '../../user/auth/auth.actions';
import { SystemProcessLoadingStatus } from 'src/app/models/global-classes';
import { OfferItem } from 'src/app/components/user-dashboard/offer/models/offer';
import { CourseHierarchyItem } from 'src/app/components/user-dashboard/product/models/course';
import { clearCurrentProduct } from '../products.actions';
import {
  DeleteCourseItemActions,
  UpdateCourseItemActions,
  loadCourseItemFullDetails,
} from './course-items/course-items.actions';
import {
  CreateQuizQuestionActions,
  DeleteQuizQuestionActions,
  MoveQuizQuestionActions,
  UpdateQuizQuestionActions,
} from './course-items/course-quiz-item/course-quiz-items.actions';

export type GenerationStatus = SystemProcessLoadingStatus; // Use your existing type

export interface CourseGenerationTracker {
  status: GenerationStatus;
  completed: any;
  total: any;
  error?: any;
  lastUpdatedLessonId?: number;
}

export interface CourseGenerationProgressMap {
  [courseId: string | number]: CourseGenerationTracker; // Use string or number based on your ID type
}
// Define the state interface for the course
export interface CourseState {
  currentCourseHeirarchy: CourseHeirarchyModel;
  currentCourseHeirarchyLoadProcess: {
    status: SystemProcessLoadingStatus;
    loadingMessage?: string;
    error: { processType: SystemProcessLoadingStatus; error: any };
  };
  generationProgress: CourseGenerationProgressMap;

  loaded: boolean;
  error: any;
}

// Define the initial state for the course
const initialState: CourseState = {
  currentCourseHeirarchy: {
    hierarchy: { items: [] },
    heirarchy_source: null,
    heirarchy_status: null,
    settings: null,
    usage_count: null,
  },
  currentCourseHeirarchyLoadProcess: { status: 'none', error: null },
  generationProgress: {},
  loaded: false,
  error: null,
};

// Create the course reducer
export const courseReducer = createReducer(
  initialState,

  // Load Current Course Hierarchy
  on(CurrentCourseHeirarchyActions.load, (state) => ({
    ...state,
    currentCourseHeirarchyLoadProcess: { status: 'loading', error: null },
  })),
  on(CurrentCourseHeirarchyActions.loadSuccess, (state, { heirarchy }) => ({
    ...state,
    currentCourseHeirarchyLoadProcess: { status: 'done', error: null },
    currentCourseHeirarchy: {
      ...state.currentCourseHeirarchy,
      hierarchy: heirarchy.hierarchy,
      heirarchy_status: heirarchy.heirarchy_status,
      heirarchy_source: heirarchy.heirarchy_source,
      settings: heirarchy.settings,
      usage_count: heirarchy.usage_count,
    },
  })),
  on(CurrentCourseHeirarchyActions.loadFailure, (state, { error }) => ({
    ...state,
    error,
    currentCourseHeirarchyLoadProcess: {
      status: 'error',
      error: { processType: 'loading', error: error.message },
    },
  })),

  // Content Generate
  on(GenerateCourseAiHierarchyActions.generate, (state) => ({
    ...state,
    currentCourseHeirarchyLoadProcess: {
      status: 'generating',
      error: null,
      loadingMessage: 'Generating Curriculum Outline',
    },
  })),

  on(GenerateCourseAiHierarchyActions.generateFailure, (state, { error }) => ({
    ...state,
    currentCourseHeirarchyLoadProcess: {
      ...state.currentCourseHeirarchyLoadProcess,
      status: 'error',
      error: { processType: 'generating', error: error.message },
    },
  })),

  // Lessons Generation
  on(GenerateLessonAiContentActions.generateAll, (state, { courseId }) => ({
    ...state,
    // Create a new tracker entry for this courseId
    currentCourseHeirarchyLoadProcess: {
      status: 'generating',
      error: null,
      loadingMessage: 'Generating Lessons Content',
    },
    generationProgress: {
      ...state.generationProgress,
      [courseId]: {
        status: 'generating',
        completed: 0,
        total: 0, // Will be set on the first 'update' action
        error: null,
      },
    },
  })),
  on(
    GenerateLessonAiContentActions.generateUpdate,
    (state, { courseId, lessonId, completed, total }) => {
      console.log(
        'Setting [generateUpdate] action',
        courseId,
        lessonId,
        completed,
        total,
      );
      console.log('Current Generation Progress:', state.generationProgress);
      // This now assumes your action contains completed & total from the effect
      return {
        ...state,
        generationProgress: {
          ...state.generationProgress,
          [courseId]: {
            ...state.generationProgress[courseId], // Get existing
            status: 'generating',
            completed: completed,
            total: total,
            lastUpdatedLessonId: lessonId,
          },
        },
      };
    },
  ),
  on(GenerateLessonAiContentActions.generateSuccess, (state, { courseId }) => ({
    ...state,
    currentCourseHeirarchyLoadProcess: {
      status: 'done',
      error: null,
      loadingMessage: 'Lessons Content Generated',
    },
    generationProgress: {
      ...state.generationProgress,
      [courseId]: {
        ...state.generationProgress[courseId],
        status: 'done',
        // Ensure completed count matches total on success
        completed:
          state.generationProgress[courseId]?.total ||
          state.generationProgress[courseId]?.completed,
        error: null,
      },
    },
  })),

  on(
    GenerateLessonAiContentActions.generateFailure,
    (state, { courseId, error }) => {
      // This now assumes your action contains courseId from the effect
      return {
        ...state,
        currentCourseHeirarchyLoadProcess: {
          status: 'error',
          error: error,
          loadingMessage: 'Generating Lessons Content Error',
        },
        generationProgress: {
          ...state.generationProgress,
          [courseId]: {
            ...state.generationProgress[courseId],
            status: 'error',
            error: error,
          },
        },
      };
    },
  ),

  // Move Course Item
  on(MoveCourseItemActions.move, (state) => ({
    ...state,
    currentCourseHeirarchyLoadProcess: {
      ...state.currentCourseHeirarchyLoadProcess,
      status: 'updating',
      loadingMessage: 'Moving',
      error: null,
    },
  })),

  on(MoveCourseItemActions.moveFailure, (state, { error }) => ({
    ...state,
    currentCourseHeirarchyLoadProcess: {
      ...state.currentCourseHeirarchyLoadProcess,
      status: 'error',
      error: { processType: 'updating', error: error.message },
    },
  })),

  // Create Course Item
  on(CreateCourseItemActions.create, (state) => ({
    ...state,
    currentCourseHeirarchyLoadProcess: {
      ...state.currentCourseHeirarchyLoadProcess,
      status: 'creating',
      loadingMessage: 'Creating',
      error: null,
    },
  })),

  on(CreateCourseItemActions.failure, (state, { error }) => ({
    ...state,
    currentCourseHeirarchyLoadProcess: {
      ...state.currentCourseHeirarchyLoadProcess,
      status: 'error',
      error: { processType: 'creating', error: error.message },
    },
  })),

  // Generate Course Curriculum
  on(
    CurriculumSeedProductActions.generateSuccess,
    (state, { courseId, heirarchy }) => {
      return {
        ...state,
        currentCourseHeirarchyLoadProcess: {
          ...state.currentCourseHeirarchyLoadProcess,
          status: 'generating_curriculum',
          loadingMessage: 'Generating Curriculum',
          error: null,
        },
      };
    },
  ),

  // Apply Template Course to Course Heirarchy
  on(ApplyTemplateToCourseAction.set, (state) => ({
    ...state,
    currentCourseHeirarchyLoadProcess: {
      ...state.currentCourseHeirarchyLoadProcess,
      status: 'applying_template',
      loadingMessage: 'Processing your AI-generated course',
      error: null,
    },
  })),

  on(ApplyTemplateToCourseAction.failure, (state, { error }) => ({
    ...state,
    currentCourseHeirarchyLoadProcess: {
      ...state.currentCourseHeirarchyLoadProcess,
      status: 'error',
      error: { processType: 'applying_template', error: error.message },
    },
  })),

  // Update Current Course Settings
  on(UpdateCourseSettingsActions.update, (state) => ({
    ...state,
    currentCourseHeirarchyLoadProcess: {
      ...state.currentCourseHeirarchyLoadProcess,
      status: 'updating_settings',
      loadingMessage: 'Updating Course Settings',
      error: null,
    },
  })),

  on(UpdateCourseSettingsActions.failure, (state, { error }) => ({
    ...state,
    currentCourseHeirarchyLoadProcess: {
      ...state.currentCourseHeirarchyLoadProcess,
      status: 'error',
      error: { processType: 'updating_settings', error: error.message },
    },
  })),

  // From Outside Actions

  on(loadCourseItemFullDetails.success, (state, { loadedItem }) => {
    const updatedHierarchy = updateParentAndNestedItem(
      state.currentCourseHeirarchy.hierarchy.items,
      loadedItem,
    );
    return {
      ...state,
      currentCourseHeirarchy: {
        ...state.currentCourseHeirarchy,
        hierarchy: {
          ...state.currentCourseHeirarchy.hierarchy,
          items: updatedHierarchy,
        },
      },
    };
  }),

  on(UpdateCourseItemActions.success, (state, { updatedItem }) => {
    console.log('updatedItem', updatedItem);
    const updatedHierarchy = updateParentAndNestedItem(
      state.currentCourseHeirarchy.hierarchy.items,
      updatedItem,
    );

    return {
      ...state,
      currentCourseHeirarchy: {
        ...state.currentCourseHeirarchy,
        hierarchy: {
          ...state.currentCourseHeirarchy.hierarchy,
          items: updatedHierarchy,
        },
      },
    };
  }),

  on(CreateQuizQuestionActions.success, (state, { updatedItem }) => {
    const updatedHierarchy = updateParentAndNestedItem(
      state.currentCourseHeirarchy.hierarchy.items,
      updatedItem,
    );

    return {
      ...state,
      currentCourseHeirarchy: {
        ...state.currentCourseHeirarchy,
        hierarchy: {
          ...state.currentCourseHeirarchy.hierarchy,
          items: updatedHierarchy,
        },
      },
    };
  }),

  on(MoveQuizQuestionActions.success, (state, { updatedItem }) => {
    const updatedHierarchy = updateParentAndNestedItem(
      state.currentCourseHeirarchy.hierarchy.items,
      updatedItem,
    );

    return {
      ...state,
      currentCourseHeirarchy: {
        ...state.currentCourseHeirarchy,
        hierarchy: {
          ...state.currentCourseHeirarchy.hierarchy,
          items: updatedHierarchy,
        },
      },
    };
  }),

  on(UpdateQuizQuestionActions.success, (state, { updatedItem }) => {
    const updatedHierarchy = updateParentAndNestedItem(
      state.currentCourseHeirarchy.hierarchy.items,
      updatedItem,
    );

    return {
      ...state,
      currentCourseHeirarchy: {
        ...state.currentCourseHeirarchy,
        hierarchy: {
          ...state.currentCourseHeirarchy.hierarchy,
          items: updatedHierarchy,
        },
      },
    };
  }),

  on(
    DeleteQuizQuestionActions.success,
    (state, { updatedItem, quizId, questionId }) => {
      const updatedHierarchy = removeQuestionFromHierarchy(
        state.currentCourseHeirarchy.hierarchy.items,
        quizId,
        questionId,
        updatedItem,
      );

      return {
        ...state,
        currentCourseHeirarchy: {
          ...state.currentCourseHeirarchy,
          hierarchy: {
            ...state.currentCourseHeirarchy.hierarchy,
            items: updatedHierarchy,
          },
        },
      };
    },
  ),

  // Generic Actions
  on(CurrentCourseHeirarchyActions.resetProcessingStatus, (state) => ({
    ...state,
    currentCourseHeirarchyLoadProcess: {
      ...state.currentCourseHeirarchyLoadProcess,
      status: 'none',
      loadingMessage: 'Updating',
      error: null,
    },
  })),

  on(updateCurrentCourseHeirarchyStructure, (state, { heirarchy }) => {
    const mergedHierarchyItems = mergeHierarchyPreservingData(
      state.currentCourseHeirarchy.hierarchy.items,
      heirarchy.hierarchy.items,
    );

    return {
      ...state,
      currentCourseHeirarchyLoadProcess: {
        ...state.currentCourseHeirarchyLoadProcess,
        status: 'done',
        loadingMessage: null,
        error: null,
      },
      currentCourseHeirarchy: {
        ...state.currentCourseHeirarchy,
        hierarchy: {
          ...heirarchy.hierarchy,
          items: mergedHierarchyItems,
        },
        heirarchy_status: heirarchy.heirarchy_status,
        heirarchy_source: heirarchy.heirarchy_source,
        settings: heirarchy.settings,
        usage_count: heirarchy.usage_count,
      },
    };
  }),
  on(
    SessionActions.forget,
    clearCurrentProduct,
    clearCurrentCourse,
    () => initialState,
  ),
);

//Separate Functions
const indexItems = (items: any[]) => {
  const index = new Map<string, any>();
  const walk = (list: any[]) => {
    for (const item of list) {
      index.set(`${item.id}-${item.type}`, item);
      if (item.items?.length) walk(item.items);
    }
  };
  walk(items);
  return index;
};

const mergeHierarchyPreservingData = (oldItems: any[], newItems: any[]) => {
  const oldItemMap = indexItems(oldItems);

  const merge = (list: any[]): any[] => {
    return list.map((newItem) => {
      const oldItem = oldItemMap.get(`${newItem.id}-${newItem.type}`);
      let mergedItem = { ...newItem };

      if (oldItem) {
        // Preserve questions for quizzes if missing in new item
        if (
          newItem.type === CourseHierarchyItemTypeEnum.quiz &&
          !newItem.questions &&
          oldItem.questions
        ) {
          mergedItem.questions = oldItem.questions;
        }
      }

      if (newItem.items?.length) {
        mergedItem.items = merge(newItem.items);
      }
      return mergedItem;
    });
  };

  return merge(newItems);
};

const updateNestedItem = (items, updatedItem) => {
  return items.map((item) => {
    if (item.id === updatedItem.id && item.type === updatedItem.type) {
      return { ...item, ...updatedItem };
    }
    if (item.items) {
      return { ...item, items: updateNestedItem(item.items, updatedItem) };
    }
    return item;
  });
};

const updateParentAndNestedItem = (items, updatedItem) => {
  return items.map((item) => {
    if (item.id === updatedItem.id && item.type === updatedItem.type) {
      return {
        ...item,
        ...updatedItem,
        items: updateNestedItem(item.items || [], updatedItem),
      };
    }
    if (item.id === updatedItem.parentId) {
      return { ...item, items: updateNestedItem(item.items, updatedItem) };
    }
    if (item.items) {
      return {
        ...item,
        items: updateParentAndNestedItem(item.items, updatedItem),
      };
    }
    return item;
  });
};

const removeQuestionFromHierarchy = (
  items: any[],
  quizId: number,
  questionId: number,
  updatedItem: any,
) => {
  return items.map((item) => {
    if (item.id === quizId && item.type === CourseHierarchyItemTypeEnum.quiz) {
      const newQuestions = item.questions
        ? item.questions.filter((q) => q.id !== questionId)
        : [];
      return {
        ...item,
        ...updatedItem,
        questions: newQuestions,
      };
    }
    if (item.items) {
      return {
        ...item,
        items: removeQuestionFromHierarchy(
          item.items,
          quizId,
          questionId,
          updatedItem,
        ),
      };
    }
    return item;
  });
};
